import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StorageInfo } from '@primatoo-ms-organization';
import { LoggedUser, PtPermissions } from '@pt/pt-models';
import { ApiClientConfig } from './api-client-config';
import { ApiClientUtils } from '../api-client.utils';
import { LoginResponse } from 'it/primatoo/openapi/api/adm/schemas/login/LoginResponse';
import { AuthPermissionsUtils } from '../auth-permissions.utils';
import {
  UpdateOrganizationSettingsRequest
} from 'it/primatoo/openapi/api/org/schemas/organization/UpdateOrganizationSettingsRequest';
import { UpdateOrganizationRequest } from 'it/primatoo/openapi/api/org/schemas/organization/UpdateOrganizationRequest';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly endpoint: string;
  private readonly endpointOrgV2: string;
  private readonly endpointAdmV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    private httpClient: HttpClient
  ) {
    this.endpoint = this.config.endPoint;
    this.endpointOrgV2 = this.config.endPoint + '/api/org/v2/';
    this.endpointAdmV2 = this.config.endPoint + '/api/adm/v2/';
  }

  public authSelf(): Observable<LoginResponse> {
    const url = this.endpointOrgV2 + `auth/self/`;
    return this.httpClient.get<LoginResponse>(url);
  }

  // V2
  public login(
    domain: string,
    username: string,
    password: string,
    otpCode: string,
    rcToken: string
  ): Observable<LoggedUser> {
    const url = this.endpointOrgV2 + 'auth/login';
    const body = { username, password, otpCode };
    const header = ApiClientUtils.buildAndAppendHttpHeaders({
      'X-Domain': domain,
      'X-Recaptcha': rcToken,
    });

    return this.httpClient
      .post<LoginResponse>(url, body, { headers: header })
      .pipe(
        map((response: LoginResponse) => {
          const enumeratedPermissions: PtPermissions[] =
            AuthPermissionsUtils.buildPermissions(
              response.system,
              response.permissions
            );
          const out: LoggedUser = {
            ...response,
            email: response.email,
            enumeratedPermissions,
          };
          return out;
          // return {
          //   ...response,
          //   enumeratedPermissions
          // };
        })
      );
  }

  public loginAsSystem(
    domain: string,
    username: string,
    password: string,
    otpCode: string,
    rcToken: string
  ): Observable<LoggedUser> {
    const url = this.endpointAdmV2 + 'auth/login-org-as-system';
    const body = { username, password, otpCode };
    const header = ApiClientUtils.buildAndAppendHttpHeaders({
      'X-Domain': domain,
      'X-Recaptcha': rcToken,
    });

    return this.httpClient
      .post<LoginResponse>(url, body, { headers: header })
      .pipe(
        map((response: LoginResponse) => {
          const enumeratedPermissions: PtPermissions[] =
            AuthPermissionsUtils.buildPermissions(
              response.system,
              response.permissions
            );
          return {
            ...response,
            email: response.email,
            enumeratedPermissions,
          };
        })
      );
  }

  public loginToConsole(
    username: string,
    password: string,
    otpCode: string,
    rcToken: string
  ): Observable<LoginResponse> {
    const url = this.endpointAdmV2 + `auth/login`;
    const body = { username, password, otpCode };
    const header = ApiClientUtils.buildAndAppendHttpHeaders({
      'X-Recaptcha': rcToken,
    });
    const httpRequest = this.httpClient.post<LoginResponse>(url, body, {
      headers: header,
    });

    return httpRequest.pipe(
      map((response: LoginResponse) => {
        const enumeratedPermissions: PtPermissions[] =
          AuthPermissionsUtils.buildPermissions(
            response.system,
            response.permissions
          );
        return {
          ...response,
          email: response.email,
          enumeratedPermissions,
        };
      })
    );
  }

  // V1

  public logout(domain: string) {
    if (domain === 'console') {
      return this.admLogout();
    } else {
      return this.orgLogout(domain);
    }
  }

  private orgLogout(domain: string) {
    let header: HttpHeaders = new HttpHeaders();
    header = header.set('X-Domain', domain);
    const url = this.endpoint + `/api/org/auth/logout`;
    return this.httpClient.post(url, {}, { headers: header });
  }

  private admLogout() {
    const url = this.endpoint + `/api/adm/auth/logout`;
    return this.httpClient.post(url, {});
  }

  public getOrganizationStorageInfo(): Observable<StorageInfo> {
    const url = this.endpoint + `/api/org/organizations/storage-info/`;
    return this.httpClient.get<StorageInfo>(url);
  }

  public updateOrganization(
    organization: UpdateOrganizationRequest
  ): Observable<void> {
    const url = this.endpointOrgV2 + `organizations/self`;
    return this.httpClient.put<void>(url, organization);
  }

  public patchOrganizationLimits(
    request: UpdateOrganizationSettingsRequest
  ): Observable<void> {
    const url = this.endpointOrgV2 + `organizations/settings/`;
    return this.httpClient.patch<void>(url, request);
  }
}
